import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClipSummary } from '@2x3/shared';
import { api } from '../lib/api.js';
import { loadDraft } from '../lib/drafts.js';
import { formatDuration } from '../lib/format.js';
import { useSession } from '../lib/session.js';

type Tab = 'todo' | 'mine' | 'all';

function CopyDots({ copies, target }: { copies: number; target: number }) {
  const dots = Math.max(copies, target);
  return (
    <span className="dots" title={`${copies} of ${target} copies`} aria-label={`${copies} of ${target} copies`}>
      {Array.from({ length: dots }, (_, i) => (
        <span key={i} className={i < copies ? 'dot is-on' : 'dot'} />
      ))}
    </span>
  );
}

/**
 * The clip list. A signed-in copier starts on "To copy": the clips they have
 * NOT done, so nobody spends an evening re-copying a clip they already sent
 * in. What they have done is on its own tab, marked in every view, and a clip
 * with an unsaved draft in this browser says so.
 */
export default function Clips() {
  const { me } = useSession();
  const [clips, setClips] = useState<ClipSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('todo');

  useEffect(() => {
    api.clips().then(setClips, (err: Error) => setError(err.message));
  }, [me?.callsign]);

  const drafts = useMemo(() => new Set((clips ?? []).filter((c) => !c.mine && loadDraft(c.id)).map((c) => c.id)), [clips]);

  const counts = useMemo(() => {
    const all = clips ?? [];
    return {
      todo: all.filter((c) => !c.mine && c.status === 'open').length,
      mine: all.filter((c) => c.mine).length,
      all: all.length,
    };
  }, [clips]);

  const shown = useMemo(() => {
    const all = clips ?? [];
    const view = !me ? all : tab === 'todo' ? all.filter((c) => !c.mine && c.status === 'open') : tab === 'mine' ? all.filter((c) => c.mine) : all;
    // Open before closed; clips you still need before the ones you have done;
    // then fewest copies first, so copies spread across the batch.
    return [...view].sort(
      (a, b) =>
        Number(a.status === 'closed') - Number(b.status === 'closed') ||
        Number(a.mine) - Number(b.mine) ||
        Number(drafts.has(b.id)) - Number(drafts.has(a.id)) ||
        a.copies - b.copies,
    );
  }, [clips, me, tab, drafts]);

  return (
    <div className="clips-page">
      <div className="page-head">
        <h1>Clips</h1>
        <p className="muted">
          Fewest copies first. Speeds are rough, measured from the keying, and a QSO's two operators rarely send at the
          same speed.
        </p>
      </div>

      {me && clips && (
        <div className="tabs" role="tablist" aria-label="Which clips">
          <button type="button" role="tab" aria-selected={tab === 'todo'} className={tab === 'todo' ? 'is-on' : ''} onClick={() => setTab('todo')}>
            To copy <span className="tab-n">{counts.todo}</span>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'mine'} className={tab === 'mine' ? 'is-on' : ''} onClick={() => setTab('mine')}>
            Copied by you <span className="tab-n">{counts.mine}</span>
          </button>
          <button type="button" role="tab" aria-selected={tab === 'all'} className={tab === 'all' ? 'is-on' : ''} onClick={() => setTab('all')}>
            All <span className="tab-n">{counts.all}</span>
          </button>
        </div>
      )}

      {error && <p className="alert is-error">{error}</p>}
      {!clips && !error && <p className="muted">Loading...</p>}
      {clips && clips.length === 0 && <p className="muted">No clips are up yet.</p>}
      {me && clips && clips.length > 0 && shown.length === 0 && (
        <p className="alert">
          {tab === 'todo'
            ? "You've copied every open clip. Thank you! New batches go up as they're carved."
            : "You haven't copied any clips yet."}
        </p>
      )}

      {shown.length > 0 && (
        <table className="clip-table">
          <thead>
            <tr>
              <th>Clip</th>
              <th>Band</th>
              <th>Date (UTC)</th>
              <th className="num">Length</th>
              <th className="num">Speed</th>
              <th>Copies</th>
              {me && <th>You</th>}
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} className={c.mine ? 'is-mine' : ''}>
                <td>
                  <Link to={`/clip/${c.id}`} className="mono">
                    {c.id}
                  </Link>
                </td>
                <td>{c.band}</td>
                <td>
                  {c.date} <span className="muted">{c.hours}</span>
                </td>
                <td className="num">{formatDuration(c.durationSec)}</td>
                <td className="num">{c.wpmEst ? `~${c.wpmEst} wpm` : <span className="muted">-</span>}</td>
                <td>
                  <CopyDots copies={c.copies} target={c.targetCopies} />
                  {c.status === 'closed' && <span className="badge">done</span>}
                </td>
                {me && (
                  <td className="row-note">
                    {c.mine ? (
                      <span className="badge is-good" title="You have sent in a copy of this clip">
                        &#10003; copied
                      </span>
                    ) : drafts.has(c.id) ? (
                      <span className="badge is-draft" title="An unsaved copy is waiting in this browser">
                        draft
                      </span>
                    ) : null}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
